import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, API } from "../lib/api";
import { useMember } from "../lib/member";
import { toast } from "sonner";
import {
  Flame, Headphones, HandHelping, LogOut, Loader2, Play, X, Send,
  Calendar, User, BookOpen, ExternalLink, ArrowLeft, CheckCircle2, Lock,
} from "lucide-react";
import PaymentBrand from "../components/PaymentBrand";
import SocialFollow from "../components/SocialFollow";

const ALT_PAYMENTS = [
  { id: "p-cashapp", brand: "cashapp", label: "Cash App", sub: "$RevivalFireGlobal", href: "https://cash.app/$RevivalFireGlobal" },
  { id: "p-paypal", brand: "paypal", label: "PayPal", sub: "@Revivalfireteam", href: "https://paypal.me/Revivalfireteam" },
  { id: "p-zelle", brand: "zelle", label: "Zelle", sub: "RevivalFireteam@gmail.com", href: "mailto:RevivalFireteam@gmail.com?subject=Zelle%20Giving" },
];

const fmtDate = (d) => { try { return new Date(d).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" }); } catch { return d; } };
const fmtUSD = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
function toDrivePreview(link) {
  if (!link) return null;
  const m = link.match(/\/file\/d\/([a-zA-Z0-9_-]+)/);
  if (m) return `https://drive.google.com/file/d/${m[1]}/preview`;
  const m2 = link.match(/[?&]id=([a-zA-Z0-9_-]+)/);
  if (m2) return `https://drive.google.com/file/d/${m2[1]}/preview`;
  return null;
}

const TABS = [
  { id: "prayer", label: "Prayer", icon: Flame },
  { id: "events", label: "Events", icon: Calendar },
  { id: "recordings", label: "Recordings", icon: Headphones },
  { id: "give", label: "Give", icon: HandHelping },
];

const Portal = () => {
  const navigate = useNavigate();
  const { member, logout } = useMember();
  const [tab, setTab] = useState("prayer");
  const doLogout = async () => { await logout(); navigate("/portal/login", { replace: true }); };

  return (
    <div className="min-h-screen bg-[#050505] text-[#F6F0E6]">
      {/* Top bar */}
      <header className="border-b border-[#C9A44C]/15 bg-[#080808]/85 backdrop-blur-xl sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <a href="/" className="flex items-center gap-3">
            <span className="icon-badge !h-9 !w-9"><Flame className="h-4 w-4 text-[#F28C18]" /></span>
            <div>
              <div className="text-[9px] tracking-[0.28em] uppercase text-[#C9A44C]">Member Portal</div>
              <div className="font-serif-display text-lg text-[#F6F0E6] -mt-0.5">RFMG Connect</div>
            </div>
          </a>
          <div className="flex items-center gap-3">
            <div className="hidden sm:flex items-center gap-2 text-xs text-[#D9D9D9]">
              {member?.picture ? <img src={member.picture} alt="" className="h-7 w-7 rounded-full object-cover border border-[#C9A44C]/30" /> : null}
              <span className="truncate max-w-[160px]">{member?.name || member?.email}</span>
            </div>
            <button onClick={doLogout} className="h-9 w-9 rounded-full border border-[#C9A44C]/30 inline-flex items-center justify-center text-[#D9D9D9] hover:text-[#E8452C]" data-testid="portal-logout">
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-10 pb-24">
        {/* Welcome */}
        <section className="mb-8">
          <div className="overline">Welcome back</div>
          <h1 className="font-serif-display text-4xl sm:text-5xl text-[#F6F0E6] mt-1 leading-[1.05]">
            {member?.name?.split(" ")[0] || "Saint"}, <span className="italic gold-text">carry the fire</span>.
          </h1>
        </section>

        <div className="bento-card !p-4 mb-8 flex items-center gap-3 border-[#F28C18]/30" data-testid="portal-livechat-soon">
          <span className="glow-blob" />
          <span className="icon-badge !h-9 !w-9"><Flame className="h-4 w-4 text-[#F28C18]" /></span>
          <div className="text-sm text-[#F6F0E6] flex-1">
            <span className="font-medium">Live Chat with Leadership — Coming Soon.</span>
            <span className="text-[#D9D9D9]/70"> Until then, your prayer request below is read and personally responded to.</span>
          </div>
        </div>

        {/* Tab bar */}
        <nav className="flex flex-wrap gap-2 mb-8" data-testid="portal-tabs">
          {TABS.map(({ id, label, icon: Icon }) => (
            <button key={id} onClick={() => setTab(id)}
              className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-full border text-sm transition-all ${tab === id ? "border-[#C9A44C] bg-[#C9A44C]/12 text-[#F6F0E6]" : "border-[#C9A44C]/25 text-[#D9D9D9] hover:border-[#C9A44C]/55"}`}
              data-testid={`portal-tab-${id}`}>
              <Icon className="h-4 w-4" strokeWidth={1.6} />
              {label}
            </button>
          ))}
        </nav>

        {tab === "prayer" && <PrayerTab member={member} />}
        {tab === "events" && <EventsTab />}
        {tab === "recordings" && <RecordingsTab />}
        {tab === "give" && <GiveTab member={member} />}

        {/* Social follow — every tab */}
        <div className="mt-14 pt-10 border-t border-[#C9A44C]/12">
          <div className="text-center mb-6">
            <div className="overline mb-2">Stay connected</div>
            <h2 className="font-serif-display text-2xl text-[#F6F0E6]">Follow Revival Fire Ministries Global</h2>
            <p className="mt-2 text-sm text-[#D9D9D9]/70">Prayer, prophetic teaching, recordings, and revival moments — every platform.</p>
          </div>
          <div className="flex justify-center">
            <SocialFollow variant="compact" />
          </div>
        </div>
      </main>
    </div>
  );
};

// ────────────────── Prayer Tab ──────────────────
const EventsTab = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/events/upcoming?limit=20")
      .then((r) => setEvents(r.data?.events || []))
      .catch(() => setEvents([]))
      .finally(() => setLoading(false));
  }, []);

  const fmt = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    return d.toLocaleString("en-US", {
      weekday: "short", month: "short", day: "numeric",
      hour: "numeric", minute: "2-digit",
    });
  };

  const linkFromDescription = (text = "") => (text.match(/https?:\/\/[^\s<>"]+/i) || [])[0];

  if (loading) {
    return <div className="text-[#D9D9D9]/60 flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Loading upcoming events…</div>;
  }

  return (
    <div className="space-y-5" data-testid="portal-events">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="overline">Upcoming gatherings</div>
          <h2 className="font-serif-display text-2xl text-[#F6F0E6] mt-1">From the official RFMG calendars</h2>
          <p className="mt-1 text-sm text-[#D9D9D9]/70">Tent revivals, prayer gatherings, services, prophetic meetings — all in one feed.</p>
        </div>
      </div>

      {events.length === 0 ? (
        <div className="bento-card !p-10 text-center" data-testid="portal-events-empty">
          <span className="glow-blob" />
          <Calendar className="h-8 w-8 text-[#C9A44C] mx-auto mb-3" />
          <h3 className="font-serif-display text-2xl text-[#F6F0E6]">No upcoming events yet</h3>
          <p className="mt-2 text-sm text-[#D9D9D9]/70">The moment leadership posts a new gathering, it appears here.</p>
        </div>
      ) : (
        <ul className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {events.map((e) => {
            const join = linkFromDescription(e.description) || linkFromDescription(e.location);
            const clean = (e.description || "").replace(/https?:\/\/[^\s<>"]+/gi, "").trim();
            return (
              <li key={e.uid || e.start_iso + e.title} className="bento-card !p-6" data-testid={`portal-event-${(e.uid || e.title).slice(0,20)}`}>
                <span className="glow-blob" />
                <div className="overline mb-2 inline-flex items-center gap-1.5">
                  <Calendar className="h-3 w-3" /> {fmt(e.start_iso)}
                </div>
                <h3 className="font-serif-display text-xl text-[#F6F0E6] leading-tight">{e.title}</h3>
                {e.location && (
                  <div className="mt-2 text-xs text-[#D9D9D9]/75 flex items-center gap-1.5">
                    <span>📍</span>{e.location}
                  </div>
                )}
                {clean && <p className="mt-3 text-sm text-[#D9D9D9]/85 line-clamp-3 whitespace-pre-line">{clean}</p>}
                {join && (
                  <a href={join} target="_blank" rel="noopener noreferrer"
                     className="mt-4 inline-flex items-center gap-1.5 text-xs text-[#C9A44C] hover:text-[#F6E4B0]"
                     data-testid="portal-event-join">
                    <ExternalLink className="h-3 w-3" /> Join / details
                  </a>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};


const PrayerTab = ({ member }) => {
  const [form, setForm] = useState({ request: "", private: true, join_updates: false });
  const [mine, setMine] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try { const r = await api.get("/portal/my-prayer-requests"); setMine(r.data.items || []); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.request.trim()) { toast.error("Please share what's on your heart."); return; }
    setSaving(true);
    try {
      await api.post("/portal/prayer-requests", {
        name: member?.name || "", email: member?.email || "",
        request: form.request, private: form.private, join_updates: form.join_updates,
      });
      toast.success("Your prayer request has been received. We are standing with you.");
      setForm({ request: "", private: true, join_updates: false });
      load();
    } catch { toast.error("Couldn't submit. Try again."); }
    finally { setSaving(false); }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
      <form onSubmit={submit} className="bento-card !p-7 lg:col-span-7 space-y-5" data-testid="portal-prayer-form">
        <span className="glow-blob" />
        <div><div className="overline">New Request</div><h2 className="font-serif-display text-2xl text-[#F6F0E6] mt-1">Submit a prayer request</h2></div>
        <div>
          <label className="field-label">Prayer Request</label>
          <textarea rows={5} className="input-fire" placeholder="Share what's on your heart…" value={form.request} onChange={(e) => setForm({ ...form, request: e.target.value })} data-testid="portal-prayer-text" />
        </div>
        <div className="space-y-2">
          <label className="check-wrap"><input type="checkbox" checked={form.private} onChange={(e) => setForm({ ...form, private: e.target.checked })} /> Keep this request private.</label>
          <label className="check-wrap"><input type="checkbox" checked={form.join_updates} onChange={(e) => setForm({ ...form, join_updates: e.target.checked })} /> Also add me to RFMG Updates.</label>
        </div>
        <button type="submit" disabled={saving} className="btn-fire w-full" data-testid="portal-prayer-submit">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          {saving ? "Submitting…" : "Submit Prayer Request"}
        </button>
      </form>

      <section className="lg:col-span-5">
        <div className="overline mb-3">Your past requests</div>
        {loading ? <div className="text-[#D9D9D9]/60"><Loader2 className="inline h-4 w-4 animate-spin mr-2" /> Loading…</div>
         : mine.length === 0 ? <p className="text-sm text-[#D9D9D9]/60">No previous requests yet.</p>
         : (
          <ul className="space-y-3">
            {mine.map((p) => (
              <li key={p.id} className="bento-card !p-5" data-testid={`my-prayer-${p.id}`}>
                <span className="glow-blob" />
                <div className="flex items-center gap-2 text-[10px] tracking-[0.22em] uppercase text-[#C9A44C]">
                  <Calendar className="h-3 w-3" /> {fmtDate(p.created_at)}
                  {p.private && <span className="inline-flex items-center gap-1 text-[#F28C18] ml-2"><Lock className="h-3 w-3" /> Private</span>}
                </div>
                <p className="mt-2 text-sm text-[#F6F0E6] line-clamp-3 whitespace-pre-wrap">{p.request}</p>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
};

// ────────────────── Recordings Tab ──────────────────
const RecordingsTab = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [playing, setPlaying] = useState(null);

  useEffect(() => {
    api.get("/portal/recordings").then((r) => setItems(r.data.recordings || [])).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="text-[#D9D9D9]/60"><Loader2 className="inline h-4 w-4 animate-spin mr-2" /> Loading recordings…</div>;
  if (items.length === 0) return <p className="text-sm text-[#D9D9D9]/70">No recordings available yet. Check back soon.</p>;

  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-testid="portal-recordings">
        {items.map((r) => (
          <article key={r.id} className="bento-card !p-6 flex flex-col" data-testid={`portal-rec-${r.id}`}>
            <span className="glow-blob" />
            <div className="flex items-start justify-between">
              <span className="icon-badge"><Headphones className="h-5 w-5 text-[#C9A44C]" /></span>
              <span className="text-[10px] tracking-[0.22em] uppercase text-[#C9A44C]">{r.service_type}</span>
            </div>
            <h3 className="mt-5 font-serif-display text-2xl text-[#F6F0E6] leading-tight">{r.title}</h3>
            <p className="mt-2 text-sm text-[#D9D9D9] line-clamp-3">{r.description}</p>
            <div className="mt-4 flex flex-wrap gap-x-4 gap-y-1.5 text-[12px] text-[#D9D9D9]/80">
              <span className="inline-flex items-center gap-1.5"><User className="h-3.5 w-3.5 text-[#C9A44C]" />{r.speaker}</span>
              <span className="inline-flex items-center gap-1.5"><Calendar className="h-3.5 w-3.5 text-[#C9A44C]" />{fmtDate(r.service_date || r.date)}</span>
              {r.scripture && <span className="inline-flex items-center gap-1.5"><BookOpen className="h-3.5 w-3.5 text-[#C9A44C]" />{r.scripture}</span>}
            </div>
            <div className="mt-auto pt-5">
              <button onClick={() => setPlaying(r)} className="btn-fire !py-2.5 !px-5 !text-sm" data-testid={`portal-play-${r.id}`}>
                <Play className="h-3.5 w-3.5" /> Watch / Listen
              </button>
            </div>
          </article>
        ))}
      </div>

      {playing && (
        <div className="fixed inset-0 z-50 bg-[#050505]/92 backdrop-blur-md flex items-center justify-center px-4 py-6" onClick={() => setPlaying(null)}>
          <div onClick={(e) => e.stopPropagation()} className="bento-card !p-5 max-w-4xl w-full" data-testid="portal-player">
            <span className="glow-blob" />
            <div className="flex items-start justify-between gap-4 mb-4 px-2">
              <div>
                <div className="overline">{playing.service_type}</div>
                <h3 className="font-serif-display text-2xl text-[#F6F0E6]">{playing.title}</h3>
                <div className="text-xs text-[#D9D9D9]/70 mt-1">{playing.speaker} · {fmtDate(playing.service_date || playing.date)}</div>
              </div>
              <button onClick={() => setPlaying(null)} className="h-9 w-9 rounded-full border border-[#C9A44C]/30 inline-flex items-center justify-center"><X className="h-4 w-4" /></button>
            </div>
            {toDrivePreview(playing.link || playing.google_drive_url) ? (
              <div className="aspect-video w-full rounded-xl overflow-hidden border border-[#C9A44C]/20 bg-[#050505]">
                <iframe title={playing.title} src={toDrivePreview(playing.link || playing.google_drive_url)} allow="autoplay; encrypted-media" allowFullScreen className="w-full h-full" />
              </div>
            ) : (
              <div className="p-8 text-center text-[#D9D9D9]">
                <a href={playing.link || playing.google_drive_url} target="_blank" rel="noopener noreferrer" className="btn-fire">
                  <ExternalLink className="h-4 w-4" /> Open in Drive
                </a>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

// ────────────────── Give Tab ──────────────────
const GiveTab = ({ member }) => {
  const [packages, setPackages] = useState([]);
  const [selected, setSelected] = useState(null);
  const [custom, setCustom] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [history, setHistory] = useState([]);

  useEffect(() => {
    api.get("/public-config").then((r) => {
      setPackages(r.data.donation_packages || []);
      const def = r.data.donation_packages?.find((p) => p.amount_usd === 50);
      setSelected(def?.id || r.data.donation_packages?.[0]?.id);
    });
    api.get("/portal/my-donations").then((r) => setHistory(r.data.donations || [])).catch(() => {});
  }, []);

  const give = async (e) => {
    e.preventDefault();
    const payload = {
      origin_url: window.location.origin,
      donor_name: member?.name || "",
      donor_email: member?.email || "",
      note: note || undefined,
    };
    if (selected === "__custom__") {
      const amt = parseFloat(custom);
      if (!amt || amt < 1) { toast.error("Enter a valid amount."); return; }
      payload.custom_amount = amt;
    } else payload.package_id = selected;

    setBusy(true);
    try {
      const r = await api.post("/portal/donations/checkout", payload);
      if (r.data?.url) window.location.href = r.data.url;
      else toast.error("Couldn't start checkout.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Checkout failed.");
    } finally { setBusy(false); }
  };

  const fmt = (n) => `$${Number(n).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

  return (
    <div className="space-y-8">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <form onSubmit={give} className="bento-card !p-7 lg:col-span-7 space-y-5" data-testid="portal-give-form">
          <span className="glow-blob" />
          <div className="flex items-center gap-3">
            <PaymentBrand name="stripe" size={36} />
            <div>
              <div className="overline">Partner With the Fire</div>
              <h2 className="font-serif-display text-2xl text-[#F6F0E6]">Give by card — secure checkout</h2>
            </div>
          </div>
          <div>
            <label className="field-label">Choose an amount</label>
            <div className="grid grid-cols-3 gap-2.5 mt-2">
              {packages.map((p) => (
                <button key={p.id} type="button" onClick={() => setSelected(p.id)}
                  className={`py-3 rounded-xl border text-sm font-medium transition-all ${selected === p.id ? "border-[#C9A44C] bg-[#C9A44C]/10 text-[#F6F0E6]" : "border-[#C9A44C]/25 text-[#D9D9D9] hover:border-[#C9A44C]/55"}`}
                  data-testid={`portal-amt-${p.id}`}>
                  {fmt(p.amount_usd)}
                </button>
              ))}
              <button type="button" onClick={() => setSelected("__custom__")}
                className={`py-3 rounded-xl border text-sm font-medium ${selected === "__custom__" ? "border-[#C9A44C] bg-[#C9A44C]/10 text-[#F6F0E6]" : "border-[#C9A44C]/25 text-[#D9D9D9]"}`}
                data-testid="portal-amt-custom">Custom</button>
            </div>
            {selected === "__custom__" && (
              <input type="number" step="0.01" min="1" max="25000" className="input-fire mt-3" placeholder="USD amount" value={custom} onChange={(e) => setCustom(e.target.value)} data-testid="portal-custom-amount" />
            )}
          </div>
          <div>
            <label className="field-label">Note <span className="opacity-60 normal-case tracking-normal">(optional)</span></label>
            <input className="input-fire" placeholder="e.g. seed for tent revival" value={note} onChange={(e) => setNote(e.target.value)} />
          </div>
          <button type="submit" disabled={busy} className="btn-fire w-full" data-testid="portal-give-submit">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Flame className="h-4 w-4" />}
            {busy ? "Redirecting…" : "Continue to Secure Checkout"}
          </button>
          <p className="text-[11px] text-[#D9D9D9]/55 text-center">Powered by Stripe · Your card details never touch RFMG.</p>
        </form>

        <section className="lg:col-span-5">
          <div className="overline mb-3">Your giving history</div>
          {history.length === 0 ? <p className="text-sm text-[#D9D9D9]/60">No gifts on record yet.</p>
           : (
            <ul className="space-y-3">
              {history.map((d) => (
                <li key={d.id} className="bento-card !p-5 flex items-center justify-between" data-testid={`my-gift-${d.id}`}>
                  <span className="glow-blob" />
                  <div>
                    <div className="text-[10px] tracking-[0.22em] uppercase text-[#C9A44C]">{fmtDate(d.created_at)} · {d.source}</div>
                    <div className="font-serif-display text-2xl text-[#F6F0E6] mt-1">{fmtUSD(d.amount_usd)}</div>
                  </div>
                  <CheckCircle2 className="h-5 w-5 text-[#C9A44C]" />
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>

      {/* Alternate payment platforms — Cash App, PayPal, Zelle */}
      <section data-testid="portal-alt-payments">
        <div className="overline mb-3">Other ways to give</div>
        <p className="text-sm text-[#D9D9D9]/70 mb-4">Tithes, offerings, and partnership gifts — choose the platform that works for you.</p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {ALT_PAYMENTS.map((p) => (
            <a
              key={p.id}
              href={p.href}
              target={p.href.startsWith("http") ? "_blank" : undefined}
              rel="noopener noreferrer"
              className="bento-card !p-5 flex items-center gap-4 hover:-translate-y-0.5 transition-all"
              data-testid={p.id}
            >
              <span className="glow-blob" />
              <PaymentBrand name={p.brand} size={44} />
              <div className="flex-1">
                <div className="font-serif-display text-lg text-[#F6F0E6]">{p.label}</div>
                <div className="text-xs text-[#D9D9D9]/75 break-all">{p.sub}</div>
              </div>
              <ExternalLink className="h-4 w-4 text-[#C9A44C]/70" />
            </a>
          ))}
        </div>
      </section>
    </div>
  );
};

export default Portal;
